import { useEffect, useRef } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Task, Collection } from '../types';
import {
  TaskDelta, CollectionDelta,
  getProjectTaskDeltas, getProjectCollectionDeltas,
} from '../services/taskService';

// Real-time refresh for the project task views. Polls Dataverse on an
// interval using a modifiedon cursor (see taskService delta fetches) and
// hands changed rows to the view, which merges them into local state.
// Pausing is the caller's contract for edit safety: while isPaused()
// returns true (drag in flight, drawer or modal open, inline edit active)
// ticks are skipped entirely — the cursor doesn't move, so the deltas
// accumulate server-side and merge on the first unpaused tick instead of
// clobbering in-progress work.

export const TASK_POLL_INTERVAL_MS = 20_000;

interface UseTaskPollingOptions {
  pca: IPublicClientApplication;
  projectId: string;
  /** Evaluated at each tick — true skips the tick and defers its deltas. */
  isPaused: () => boolean;
  onTasks: (deltas: TaskDelta[]) => void;
  onCollections?: (deltas: CollectionDelta[]) => void;
  intervalMs?: number;
}

export function useTaskPolling({
  pca, projectId, isPaused, onTasks, onCollections,
  intervalMs = TASK_POLL_INTERVAL_MS,
}: UseTaskPollingOptions): void {
  // Callbacks live in a ref refreshed every render, so tick closures always
  // see current component state without restarting the interval.
  const cbRef = useRef({ isPaused, onTasks, onCollections });
  cbRef.current = { isPaused, onTasks, onCollections };
  const cursorRef = useRef<string>('');
  const inflightRef = useRef(false);

  useEffect(() => {
    // Fresh cursor per project, starting one interval back: the initial
    // full load owns everything up to mount, and the overlap only causes
    // idempotent re-merges if the two race.
    cursorRef.current = new Date(Date.now() - intervalMs).toISOString();

    const tick = async () => {
      if (inflightRef.current || document.hidden || cbRef.current.isPaused()) return;
      inflightRef.current = true;
      try {
        const since = cursorRef.current;
        const wantCollections = !!cbRef.current.onCollections;
        const [taskDeltas, collectionDeltas] = await Promise.all([
          getProjectTaskDeltas(pca, projectId, since),
          wantCollections
            ? getProjectCollectionDeltas(pca, projectId, since)
            : Promise.resolve([] as CollectionDelta[]),
        ]);
        // Advance the cursor to the newest server timestamp seen. ISO-8601
        // strings from Dataverse compare correctly lexically.
        for (const row of [...taskDeltas, ...collectionDeltas]) {
          if (row.modifiedon && row.modifiedon > cursorRef.current) {
            cursorRef.current = row.modifiedon;
          }
        }
        if (taskDeltas.length) cbRef.current.onTasks(taskDeltas);
        if (collectionDeltas.length) cbRef.current.onCollections?.(collectionDeltas);
      } catch {
        // Transient (token refresh, network blip) — the next tick retries
        // from the same cursor, so nothing is lost.
      } finally {
        inflightRef.current = false;
      }
    };

    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [pca, projectId, intervalMs]);
}

/**
 * Upsert task deltas into a view's task array, re-sorted onto the canonical
 * server order (sortorder asc, createdon asc) so board columns stack the
 * same as after a full load.
 */
export function mergeTaskDeltas(prev: Task[], deltas: TaskDelta[]): Task[] {
  const map = new Map(prev.map(t => [t.dagrun_pm_taskid, t]));
  for (const d of deltas) {
    const existing = map.get(d.dagrun_pm_taskid);
    map.set(d.dagrun_pm_taskid, existing ? { ...existing, ...d } : d);
  }
  return [...map.values()].sort((a, b) =>
    ((a.dagrun_pm_sortorder ?? 999999) - (b.dagrun_pm_sortorder ?? 999999))
    || (a.createdon ?? '').localeCompare(b.createdon ?? ''));
}

/**
 * Upsert collection deltas. Existing collections update in place (their
 * position may be a personal order — never resequence it); new ones append
 * in global sortorder order and settle on the next full load.
 */
export function mergeCollectionDeltas(prev: Collection[], deltas: CollectionDelta[]): Collection[] {
  const known = new Set(prev.map(c => c.dagrun_pm_collectionid));
  const updated = prev.map(c => {
    const d = deltas.find(x => x.dagrun_pm_collectionid === c.dagrun_pm_collectionid);
    return d ? { ...c, ...d } : c;
  });
  const added = deltas
    .filter(d => !known.has(d.dagrun_pm_collectionid))
    .sort((a, b) => (a.dagrun_pm_sortorder ?? 0) - (b.dagrun_pm_sortorder ?? 0));
  return added.length ? [...updated, ...added] : updated;
}
